/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: @HardusLourens fix no explicit any
import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, Inject, DestroyRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Observable, Subscription, BehaviorSubject } from 'rxjs';
import { map, skipWhile, take } from 'rxjs/operators';
import { LocationPickerResult, LocationPickerTheme } from '@flexus/ui-elements';
import { getActiveTheme } from '@flexus/ux';
import { BigFormService, NetworkService } from '@flexus/core';
import { intersection } from 'ramda';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'flx-address-confirmation',
	templateUrl: './address-confirmation.component.html',
	styleUrls: ['./address-confirmation.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddressConfirmationComponent implements OnInit, OnDestroy {
	// ===========================================  Variables ===========================================================
	// public addressConfirmationForm: FormGroup;
	public locationPickerForm!: UntypedFormGroup;
	@Input() provinceList$!: Observable<{ id: number; name: string }[]>;
	@Input() policyAddress$!: Observable<any>;
	@Input() policyAddressObject$!: Observable<any>;
	@Input() addressConfirmationText: string = 'Confirm Claimant’s Address';
	@Input() addressType: string = 'Policy Address';

	public address;
	public suburb;
	public city;
	public suburb_code;
	public province;
	public latitude;
	public longitude;

	public policyAddress;
	public isInSA: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public locationSearched: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public claimDetails$!: Observable<LocationPickerResult>;
	private provinceListSubscription?: Subscription;
	private addressDetailsSubscription?: Subscription;
	private searchedAddressSubscription?: Subscription;
	public activeTheme!: Observable<LocationPickerTheme>;
	public readonly defaultTheme = LocationPickerTheme.Light;
	public policyAddressFallback = '';
	public hasMinimumAddress = false;

	private networkCheckSubscription!: Subscription;
	isOffline = false;
	offlineMessage = 'You are offline. Please come back to confirm the claimant’s address when you are back online.';

	public storeLocation!: LocationPickerResult;
	public searchedLocation!: LocationPickerResult;
	public screenData = {
		originalAddress: {
			showLatLng: false
		},
		verifiedAddress: {
			showLatLng: false
		}
	};

	initialPin!: LocationPickerResult;
	public policyAddressObjectFallback: {
		propertystreetaddress?: string;
		propertysuburb?: string;
		propertycity?: string;
		suburbcode?: string;
	} = {};

	private toNumberOrNull(value: any): number | null {
		if (value == null || value === '') return null;
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}

	private buildSavedSearchedLocation(insuredAddress: UntypedFormGroup | null): LocationPickerResult | null {
		const get = (name: string) => this.bf.bigForm.get(name)?.value;
		const foundDescription = get('address_found') ?? get('address_searched') ?? null;
		const savedSuburb = get('address_suburb') ?? insuredAddress?.value?.suburb ?? '';
		const savedCity = insuredAddress?.value?.city ?? '';
		const savedProvince = get('province') ?? insuredAddress?.value?.province ?? '';
		const savedPostal = insuredAddress?.value?.postal_code ?? get('suburb_code') ?? '';
		const savedLat = this.toNumberOrNull(get('address_to_lat') ?? get('address_from_lat') ?? get('latitude') ?? null);
		const savedLng = this.toNumberOrNull(get('address_to_lng') ?? get('address_from_lng') ?? get('longitude') ?? null);

		let address = insuredAddress?.value?.street_address ?? '';
		let suburb = savedSuburb;
		let city = savedCity;
		let suburbCode = savedPostal;

		if (typeof foundDescription === 'string' && foundDescription.trim().length > 0) {
			const parts = foundDescription
				.split(',')
				.map(part => part.trim())
				.filter(Boolean);
			address = address || parts[0] || '';
			suburb = suburb || parts[1] || '';
			city = city || parts[2] || '';
			suburbCode = suburbCode || parts[3] || '';
		}

		if (!address && !suburb && !city && !suburbCode && !savedLat && !savedLng) {
			return null;
		}

		return new LocationPickerResult(
			address,
			suburb,
			city,
			suburbCode,
			savedProvince ?? '',
			'South Africa',
			savedLat ?? 0,
			savedLng ?? 0
		);
	}

	constructor(
		@Inject(Store) public _store: Store<any>,
		@Inject(UntypedFormBuilder) private _fb: UntypedFormBuilder,
		public bf: BigFormService,
		private networkService: NetworkService,
		private destroyRef: DestroyRef
	) {
		this.notifyNetworkStatus();
	}

	ngOnInit() {
		this.activeTheme = this._store.select(getActiveTheme).pipe(
			map(at => {
				switch (at) {
					case 'four-sure-dark':
						return LocationPickerTheme.Dark;
					default:
						return LocationPickerTheme.Light;
				}
			})
		);

		const insured_address = this.bf.bigForm.get('insured_address') as UntypedFormGroup | null;
		const ensureControl = (name: string) => {
			if (!this.bf.bigForm.get(name)) {
				this.bf.bigForm.addControl(name, new UntypedFormControl(null));
			}
		};
		this.province = this.bf.bigForm.get('insured_address')?.value.province;

		if (!this.locationPickerForm) {
			this.locationPickerForm = new UntypedFormGroup({});
		}
		if (!this.locationPickerForm.get('address_details')) {
			this.locationPickerForm.addControl('address_details', new UntypedFormControl());
		}
		if (!this.locationPickerForm.get('searched_address')) {
			this.locationPickerForm.addControl('searched_address', new UntypedFormControl(this.searchedLocation));
		}

		const initialAddress = new LocationPickerResult(
			insured_address?.value?.street_address ?? '',
			insured_address?.value?.suburb ?? '',
			insured_address?.value?.city ?? '',
			this.bf.bigForm.get('suburb_code')?.value ?? '',
			this.bf.bigForm.get('province')?.value ?? '',
			'',
			this.toNumberOrNull(this.bf.bigForm.get('latitude')?.value ?? null) ?? 0,
			this.toNumberOrNull(this.bf.bigForm.get('longitude')?.value ?? null) ?? 0
		);
		this.locationPickerForm.patchValue({
			address_details: initialAddress
		});

		const fallbackParts = [
			insured_address?.value?.street_address,
			insured_address?.value?.suburb,
			insured_address?.value?.city,
			this.bf.bigForm.get('suburb_code')?.value
		].filter(Boolean);
		this.policyAddressFallback = fallbackParts.join(', ');
		this.policyAddressObjectFallback = {
			propertystreetaddress: insured_address?.value?.street_address ?? '',
			propertysuburb: insured_address?.value?.suburb ?? '',
			propertycity: insured_address?.value?.city ?? '',
			suburbcode: this.bf.bigForm.get('suburb_code')?.value ?? ''
		};
		this.hasMinimumAddress = !!(
			insured_address?.value?.street_address &&
			insured_address?.value?.suburb &&
			insured_address?.value?.city &&
			insured_address?.value?.postal_code
		);

		insured_address?.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value: any) => {
				const parts = [value?.street_address, value?.suburb, value?.city, this.bf.bigForm.get('suburb_code')?.value].filter(Boolean);
				this.policyAddressFallback = parts.join(', ');
				this.policyAddressObjectFallback = {
					propertystreetaddress: value?.street_address ?? '',
					propertysuburb: value?.suburb ?? '',
					propertycity: value?.city ?? '',
					suburbcode: this.bf.bigForm.get('suburb_code')?.value ?? ''
				};
				this.hasMinimumAddress = !!(value?.street_address && value?.suburb && value?.city && value?.postal_code);
			});

		this.addressDetailsSubscription = this.locationPickerForm.get('address_details')?.valueChanges?.subscribe(address_details => {
			ensureControl('address_found');
			ensureControl('address_suburb');
			ensureControl('address_from_lat');
			ensureControl('address_from_lng');
			ensureControl('addressconfirmation');

			this.bf.bigForm.patchValue({
				latitude: address_details.latitude,
				longitude: address_details.longitude,
				suburb_code: address_details.suburb_code,
				province: address_details.province,
				address_found: address_details?.description ?? null,
				address_suburb: address_details?.suburb ?? null,
				address_from_lat: address_details?.latitude ?? null,
				address_from_lng: address_details?.longitude ?? null,
				addressconfirmation: 'MapChecked'
			});
			// this.updatedLatitude.update(address_details.latitude)
			// this.updateLongitude.update(address_details.longitude)
			this.bf.patchValues({updated_latitude: address_details.latitude })
			this.bf.patchValues({updated_longitude: address_details.longitude })
			this.updateAddressForm(address_details);
			this.findAndSetProvince();
		});

		this.searchedAddressSubscription = this.locationPickerForm.get('searched_address')?.valueChanges?.subscribe(searched_address => {
			ensureControl('address_searched');
			ensureControl('address_to_lat');
			ensureControl('address_to_lng');

			this.searchedLocation = searched_address;
			this.bf.bigForm.patchValue({
				address_searched: searched_address?.description ?? null,
				address_to_lat: searched_address?.latitude ?? null,
				address_to_lng: searched_address?.longitude ?? null
			});
			this.findAndSetProvince();
		});

		// Rehydrate map search output when opening existing claims/drafts.
		// Address Found + Pinned Address blocks read from searched_address.
		const savedSearchedLocation = this.buildSavedSearchedLocation(insured_address);
		if (savedSearchedLocation) {
			this.searchedLocation = savedSearchedLocation;
			this.locationSearched.next(true);
			this.isInSA.next(true);
			this.locationPickerForm.patchValue(
				{
					searched_address: savedSearchedLocation
				},
				{ emitEvent: false }
			);
		}
	}

	updateAddressForm(loc: LocationPickerResult) {
		this.locationPickerForm.patchValue({ searched_address: loc });

		this.locationSearched.next(true);

		const iis = loc.country === 'South Africa';

		this.isInSA.next(iis);

		if (iis) this.locationPickerForm.patchValue({ addressconfirmation: 'MapChecked' });
	}

	policyAndFoundAddressPartsEqual(policyAddressPart: string, foundAddressPart: string) {
		const common = intersection(policyAddressPart?.split(' '), foundAddressPart?.split(' '));
		return common.length > 0;
	}

	private notifyNetworkStatus() {
		this.networkCheckSubscription = this.networkService.isOnline.subscribe(isOnline => {
			if (isOnline) {
				this.isOffline = false;
			} else {
				this.isOffline = true;
			}
		});
	}

	resetPin() {
		this.updateAddressForm(this.locationPickerForm?.value?.policy_address);
	}

	findAndSetProvince() {
		this.provinceListSubscription = this.provinceList$
			.pipe(
				skipWhile(res => !res),
				take(1)
			)
			.subscribe(res => {
				if (this.searchedLocation) {
					const normalizeProvince = (value: string | null | undefined) =>
						(value ?? '')
							.toLowerCase()
							.replace(/[\s-]/g, '')
							.trim();
					const searchedProvince = normalizeProvince(this.searchedLocation.province);
					const area_code = res?.find(province => normalizeProvince(province?.name) === searchedProvince)?.id;

					const area_code_form = this.bf.bigForm.get('area_code');
					if (!area_code_form) this.bf.bigForm.addControl('area_code', new UntypedFormControl(null));
					this.bf.bigForm.patchValue({ area_code });
				}
			});
	}

	setColorIndicatorColor(entryOne: string, entryTwo: string) {
		if (!entryOne?.length || !entryTwo?.length) return 'red';

		const result = new RegExp(entryOne.replace(/\s+/g, ' '), 'gi')?.test(entryTwo.replace(/\s+/g, ' '));
		return result ? 'green' : 'red';
	}

	displayPropertyWithValue(property?: string | null) {
		return property?.length ? property : 'Not Found';
	}

	compareAddressParts(entryOne?: string | null, entryTwo?: string | null) {
		return this.setColorIndicatorColor(entryOne ?? '', entryTwo ?? '');
	}

	ngOnDestroy() {
		this.findAndSetProvince();

		if (this.addressDetailsSubscription) this.addressDetailsSubscription.unsubscribe();
		if (this.searchedAddressSubscription) this.searchedAddressSubscription.unsubscribe();
		if (this.provinceListSubscription) this.provinceListSubscription.unsubscribe();
		if (this.networkCheckSubscription) this.networkCheckSubscription.unsubscribe();
	}
}
